import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { 
  DocumentTextIcon, 
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  XCircleIcon,
  UserIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { getHseDocument, getDocumentVersions, approveDocumentVersion, rejectDocumentVersion, getVersionApprovals } from "../../../../api/hse/documents";
import {
  hasEarlierApprovedVersion,
  RequiresReacknowledgementSwitch,
} from "../../../../components/dashboard/hse/compliance/ApproveRevisionDialog";

/**
 * HSE/KS Document Approval - Approval workflow
 */
export const HseKsDocumentApproval = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [pendingVersion, setPendingVersion] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Fetch document
      const docResponse = await getHseDocument(id);
      setDocument(docResponse.document);
      
      // Fetch versions
      const versionsResponse = await getDocumentVersions(id);
      setVersions(versionsResponse.versions || []);
      
      // Find pending approval version
      const pending = versionsResponse.versions?.find(v => v.status === 'pending_approval');
      setPendingVersion(pending);
      
      // Fetch approvals for pending version
      if (pending) {
        try {
          const approvalsResponse = await getVersionApprovals(id, pending.id);
          setApprovals(approvalsResponse.approvals || []);
        } catch (err) {
          console.error("Error fetching approvals:", err);
          // Don't fail the whole page if approvals can't be fetched
        }
      }
    } catch (err) {
      console.error("Error fetching document data:", err);
      setError(true);
      toast.error(t("Kunne ikke laste dokument"));
    } finally {
      setLoading(false);
    }
  };

  const [requiresReack, setRequiresReack] = useState(true);
  const showReackChoice = Boolean(
    pendingVersion && hasEarlierApprovedVersion(versions, pendingVersion.version)
  );

  const handleApprove = async () => {
    if (!pendingVersion) return;
    
    try {
      setApproving(true);
      await approveDocumentVersion(
        id,
        pendingVersion.id,
        comment || null,
        showReackChoice ? { requiresReacknowledgement: requiresReack } : {}
      );
      toast.success(t("Versjon godkjent"));
      // Refresh data
      await fetchData();
      // Navigate back to document view
      navigate(`/dashboard/hse-ks/documents/${id}`);
    } catch (err) {
      console.error("Error approving version:", err);
      const errorMessage = err.response?.data?.errors?.[0] || t("Kunne ikke godkjenne versjon");
      toast.error(errorMessage);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!pendingVersion) return;
    
    try {
      setRejecting(true);
      await rejectDocumentVersion(id, pendingVersion.id, comment || null);
      toast.success(t("Versjon avvist"));
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error rejecting version:", err);
      const errorMessage = err.response?.data?.errors?.[0] || t("Kunne ikke avvise versjon");
      toast.error(errorMessage);
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error || !document) {
    return <ResourceError errors={[t("Kunne ikke laste dokument")]} />;
  }

  if (!pendingVersion) {
    return (
      <HsePageChrome
        title={t("Godkjenning")}
        subtitle={document.title || `Dokument #${id}`}
        backButton={{
          label: t("Tilbake"),
          action: () => navigate(`/dashboard/hse-ks/documents/${id}`),
          icon: ArrowLeftIcon,
        }}
      >
        <div className="mx-auto mt-8 max-w-3xl sm:px-6">
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("Ingen versjon venter på godkjenning")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("Dette dokumentet har ingen versjoner som venter på godkjenning.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </HsePageChrome>
    );
  }

  const currentUserApproval = approvals.find(a => a.user_id === currentUser?.id);
  const approvedCount = approvals.filter(a => a.approved === true).length;
  const rejectedCount = approvals.filter(a => a.approved === false).length;
  // Workflow vote uses `approved`; read-understood uses `read_understood_at` and must not block approve UI.
  // Also allow re-vote if the version is still pending but this user already has approved=true (healed / failed promotion).
  const showWorkflowActions =
    !currentUserApproval ||
    currentUserApproval.approved == null ||
    (currentUserApproval.approved === true && pendingVersion?.status === "pending_approval");

  return (
    <HsePageChrome
      title={t("Godkjenning")}
      subtitle={document.title || `Dokument #${id}`}
      backButton={{
        label: t("Tilbake"),
        action: () => navigate(`/dashboard/hse-ks/documents/${id}`),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6">
        {/* Document Info */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                  {document.title}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                  <span className="font-medium">{document.document_type_display || document.document_type}</span>
                  <span>{t("Versjon")} {pendingVersion.version}</span>
                  {document.document_number && <span className="font-mono">{document.document_number}</span>}
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <ClockIcon className="h-3 w-3 mr-1" />
                {t("Venter på godkjenning")}
              </span>
            </div>
            
            {pendingVersion.change_summary && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">{t("Endringssammendrag")}</h4>
                <p className="text-sm text-gray-600">{pendingVersion.change_summary}</p>
              </div>
            )}
          </div>
        </div>

        {/* Approval Status */}
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              {t("Godkjenningsstatus")}
            </h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500">{t("Totalt")}</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{approvals.length}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm font-medium text-green-700">{t("Godkjent")}</div>
                <div className="mt-1 text-2xl font-semibold text-green-900">{approvedCount}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-sm font-medium text-red-700">{t("Avvist")}</div>
                <div className="mt-1 text-2xl font-semibold text-red-900">{rejectedCount}</div>
              </div>
            </div>

            {approvals.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">{t("Godkjenninger")}</h4>
                <ul className="divide-y divide-gray-200">
                  {approvals.map((approval) => (
                    <li key={approval.id} className="py-3 flex items-center justify-between">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {approval.user?.displayTitle || approval.user?.name || approval.user?.email || t("Ukjent bruker")}
                          </p>
                          {approval.comment && (
                            <p className="text-sm text-gray-500 mt-1">{approval.comment}</p>
                          )}
                          {approval.approved_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(approval.approved_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        {approval.approved === true ? (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            {t("Godkjent")}
                          </span>
                        ) : approval.approved === false ? (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            {t("Avvist")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {t("Venter")}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Approval Actions */}
        {showWorkflowActions ? (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {t("Din godkjenning")}
              </h3>
              {currentUserApproval?.approved === true && pendingVersion?.status === "pending_approval" && (
                <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {t("hse.document.approval_stuck_retry_hint")}
                </p>
              )}
              
              <div className="mb-4">
                <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Kommentar")} ({t("valgfritt")})
                </label>
                <textarea
                  id="comment"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder={t("Legg til en kommentar...")}
                />
              </div>

              {showReackChoice ? (
                <div className="mb-4">
                  <RequiresReacknowledgementSwitch
                    checked={requiresReack}
                    onChange={setRequiresReack}
                    disabled={approving || rejecting}
                  />
                </div>
              ) : null}

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={approving || rejecting}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  {rejecting ? t("Avviser...") : t("Avvis")}
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approving || rejecting}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  {approving ? t("Godkjenner...") : t("Godkjenn versjon")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                {currentUserApproval.approved === true ? (
                  <>
                    <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {t("Du har godkjent denne versjonen")}
                    </h3>
                    {currentUserApproval.comment && (
                      <p className="mt-1 text-sm text-gray-500">{currentUserApproval.comment}</p>
                    )}
                  </>
                ) : (
                  <>
                    <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {t("Du har avvist denne versjonen")}
                    </h3>
                    {currentUserApproval.comment && (
                      <p className="mt-1 text-sm text-gray-500">{currentUserApproval.comment}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </HsePageChrome>
  );
};
